import { useState } from "react";
import { Link } from "react-router-dom";

import { OrganizationPhoneNumber } from "../../redux/reducers/organizationPhoneNumberSlice";
import { OrganizationTelephony } from "../../redux/reducers/organizationTelephonySlice";
import { Agent } from "../../redux/reducers/agentSlice";
import { Button, InputField, SettingsList, SettingsListItem, Spinner, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { Icon } from "../base/Icon";
import { Form, useForm } from "../base/Form";
import { TelephonyConfigurationDropdown } from "./TelephonyConfigurationDropdown";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { IconButton } from "../base/IconButton";
import { useAppSelector } from "../../redux";
import { getTelephonyProviderInfo } from "../../utils/telephony";
import { useAuth } from "../../hooks/useAuth";

type AddMode = "purchase" | "existing";

type PhoneNumbersCardProps = {
  phoneNumbers: OrganizationPhoneNumber[];
  onPurchase: (data: { telephonyId: string; areaCode: string; countryCode: string; friendlyName: string }) => Promise<{ error?: Error }>;
  onAdd: (data: { telephonyId: string; phoneNumber: string; friendlyName: string }) => Promise<{ error?: Error }>;
  onUpdate: (data: Partial<OrganizationPhoneNumber>) => void;
  onDelete: (id: string) => void;
  onRelease: (id: string) => void;
  isPurchasing?: boolean;
  isAdding?: boolean;
  isDeleting?: boolean;
  isReleasing?: boolean;
  isLoading?: boolean;
  organizationId?: string;
};

type PurchaseFormData = {
  telephonyId: string;
  areaCode: string;
  countryCode: string;
  friendlyName: string;
};

type AddExistingFormData = {
  telephonyId: string;
  phoneNumber: string;
  friendlyName: string;
};

type EditFormData = {
  friendlyName: string;
};

export const PhoneNumbersCard = ({
  phoneNumbers,
  onPurchase,
  onAdd,
  onUpdate,
  onDelete,
  onRelease,
  isPurchasing = false,
  isAdding = false,
  isDeleting = false,
  isReleasing = false,
  isLoading = false,
  organizationId,
}: PhoneNumbersCardProps) => {
  const telephonyConfigs = useAppSelector((state) => Object.values(state.organizationTelephony.entities)) as OrganizationTelephony[];
  const agents = useAppSelector((state) => state.agent.entities) as Record<string, Agent>;
  const { isAdmin } = useAuth();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>("purchase");
  const [editingPhoneNumber, setEditingPhoneNumber] = useState<OrganizationPhoneNumber | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removingPhoneNumber, setRemovingPhoneNumber] = useState<OrganizationPhoneNumber | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const { formData: purchaseFormData, onChange: onChangePurchase } = useForm<PurchaseFormData>({
    telephonyId: "",
    areaCode: "",
    countryCode: "US",
    friendlyName: "",
  });

  const { formData: addExistingFormData, onChange: onChangeAddExisting } = useForm<AddExistingFormData>({
    telephonyId: "",
    phoneNumber: "",
    friendlyName: "",
  });

  const { formData: editFormData, onChange: onChangeEdit } = useForm<EditFormData>({
    friendlyName: "",
  });

  const resetPurchaseForm = () => {
    onChangePurchase({
      telephonyId: "",
      areaCode: "",
      countryCode: "US",
      friendlyName: "",
    });
  };

  const resetAddExistingForm = () => {
    onChangeAddExisting({
      telephonyId: "",
      phoneNumber: "",
      friendlyName: "",
    });
  };

  const resetEditForm = () => {
    onChangeEdit({ friendlyName: "" });
  };

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setAddMode("purchase");
    setPurchaseError(null);
    setAddError(null);
    resetPurchaseForm();
    resetAddExistingForm();
  };

  // Whether the selected provider in the "Add Existing" form is VoiceRun
  const isVoiceRunSelected = !addExistingFormData.telephonyId || addExistingFormData.telephonyId === "mock-voicerun";
  const canAddExisting = isAdmin || !isVoiceRunSelected;

  const onClickPurchase = async () => {
    setPurchaseError(null);
    const result = await onPurchase({
      telephonyId: purchaseFormData.telephonyId || "voicerun",
      areaCode: purchaseFormData.areaCode || "",
      countryCode: purchaseFormData.countryCode || "US",
      friendlyName: purchaseFormData.friendlyName || "",
    });
    if (result?.error) {
      setPurchaseError("Failed to purchase phone number. Please try again.");
    } else {
      closeAddDialog();
    }
  };

  const onClickAddExisting = async () => {
    setAddError(null);
    const result = await onAdd({
      telephonyId: addExistingFormData.telephonyId || "voicerun",
      phoneNumber: addExistingFormData.phoneNumber || "",
      friendlyName: addExistingFormData.friendlyName || "",
    });
    if (result?.error) {
      setAddError("You do not have access to this telephony provider or the phone number does not exist.");
    } else {
      closeAddDialog();
    }
  };

  const onClickUpdate = () => {
    if (editingPhoneNumber) {
      onUpdate({
        id: editingPhoneNumber.id,
        friendlyName: editFormData.friendlyName,
      });
      setEditingPhoneNumber(null);
      resetEditForm();
    }
  };

  const onClickEdit = (phoneNumber: OrganizationPhoneNumber) => {
    setEditingPhoneNumber(phoneNumber);
    onChangeEdit({ friendlyName: phoneNumber.friendlyName || "" });
  };

  const handleRemoveClick = (phoneNumber: OrganizationPhoneNumber) => {
    setRemovingPhoneNumber(phoneNumber);
    setShowRemoveDialog(true);
  };

  const closeRemoveDialog = () => {
    setShowRemoveDialog(false);
    setRemovingPhoneNumber(null);
  };

  const confirmDelete = () => {
    if (removingPhoneNumber) {
      onDelete(removingPhoneNumber.id);
      closeRemoveDialog();
    }
  };

  const confirmRelease = () => {
    if (removingPhoneNumber) {
      onRelease(removingPhoneNumber.id);
      closeRemoveDialog();
    }
  };

  return (
    <OrganizationSettingsTab
      title="Phone Numbers"
      count={phoneNumbers.length}
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      ) : (
        <SettingsList>
          {phoneNumbers.map((phoneNumber) => {
            const telephonyConfig = telephonyConfigs.find((c) => c.id === phoneNumber.telephonyId);
            const providerInfo = getTelephonyProviderInfo(telephonyConfig?.providerType || phoneNumber.telephonyId);
            return (
              <SettingsListItem
                key={phoneNumber.id}
                title={phoneNumber.friendlyName || phoneNumber.phoneNumber}
                subtitle={
                  <View className="flex items-center gap-1.5">
                    {phoneNumber.friendlyName && <Text className="text-sm text-gray-500 dark:text-gray-400">{phoneNumber.phoneNumber}</Text>}
                    <View className="flex items-center gap-1">
                      <Icon
                        name={providerInfo.icon}
                        size={12}
                        className="text-gray-400 dark:text-gray-500"
                      />
                      <Text className="text-xs text-gray-400 dark:text-gray-500">{telephonyConfig?.name || providerInfo.name}</Text>
                    </View>
                    {phoneNumber.agentId && phoneNumber.agentEnvironmentId ? (
                      <Link
                        to={`/agents/${phoneNumber.agentId}/environments?e=${phoneNumber.agentEnvironmentId}`}
                        className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        <Icon
                          name="link"
                          size={10}
                        />
                        {agents[phoneNumber.agentId]?.name || "Agent"}
                      </Link>
                    ) : (
                      <Text className="text-xs text-gray-400 dark:text-gray-500">Unassigned</Text>
                    )}
                  </View>
                }
                icon={providerInfo.icon}
              >
                <View className="flex items-center gap-2">
                  <IconButton
                    variant="ghost"
                    icon="edit"
                    onClick={() => onClickEdit(phoneNumber)}
                  />
                  <IconButton
                    variant="danger"
                    icon="trash"
                    onClick={() => handleRemoveClick(phoneNumber)}
                  />
                </View>
              </SettingsListItem>
            );
          })}
          <button
            onClick={() => setShowAddDialog(true)}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add phone number
            </Text>
          </button>
        </SettingsList>
      )}

      {/* Add Number Dialog */}
      <Dialog
        isOpen={showAddDialog}
        onClose={closeAddDialog}
        title="Add Phone Number"
      >
        <View className="p-4 space-y-4">
          {/* Mode Toggle */}
          <View className="flex rounded-lg p-1 bg-gray-100 dark:bg-gray-800/60">
            <button
              onClick={() => setAddMode("purchase")}
              className={`flex-1 px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                addMode === "purchase"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Purchase New
            </button>
            <button
              onClick={() => setAddMode("existing")}
              className={`flex-1 px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                addMode === "existing"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Add Existing
            </button>
          </View>

          {addMode === "purchase" ? (
            <Form
              data={purchaseFormData}
              onChange={onChangePurchase}
            >
              <TelephonyConfigurationDropdown
                value={purchaseFormData.telephonyId}
                onChange={(value) => onChangePurchase({ telephonyId: value })}
                organizationId={organizationId}
                placeholder="Select a telephony configuration"
              />

              <InputField
                name="friendlyName"
                label="Friendly Name"
                placeholder="Enter a friendly name (optional)"
              />

              <InputField
                name="areaCode"
                label="Area Code"
                placeholder="e.g. 415"
              />

              <InputField
                name="countryCode"
                label="Country Code"
                placeholder="e.g. US"
              />

              {purchaseError && (
                <View className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800">
                  <Icon
                    name="error"
                    size={14}
                    className="text-red-500 dark:text-red-400 shrink-0"
                  />
                  <Text className="text-xs text-red-600 dark:text-red-400">{purchaseError}</Text>
                </View>
              )}

              <View className="flex gap-3 pt-2">
                <Button
                  variant="success"
                  size="auto"
                  onClick={onClickPurchase}
                  disabled={isPurchasing}
                  className="flex-1 justify-center"
                >
                  {isPurchasing ? (
                    <Icon
                      name="loader"
                      size={14}
                      className="animate-spin"
                    />
                  ) : (
                    <Icon
                      name="add"
                      size={14}
                    />
                  )}
                  Purchase
                </Button>
                <Button
                  variant="neutral"
                  size="auto"
                  onClick={closeAddDialog}
                  className="flex-1 justify-center"
                >
                  Cancel
                </Button>
              </View>
            </Form>
          ) : (
            <Form
              data={addExistingFormData}
              onChange={onChangeAddExisting}
            >
              <TelephonyConfigurationDropdown
                value={addExistingFormData.telephonyId}
                onChange={(value) => onChangeAddExisting({ telephonyId: value })}
                organizationId={organizationId}
                placeholder="Select a telephony configuration"
              />

              <InputField
                name="phoneNumber"
                label="Phone Number"
                placeholder="e.g. +14155551234"
              />

              <InputField
                name="friendlyName"
                label="Friendly Name"
                placeholder="Enter a friendly name (optional)"
              />

              {!canAddExisting && (
                <Text className="text-xs text-amber-600 dark:text-amber-400">
                  You can only add existing numbers on your own telephony provider. Select your own telephony provider or use Purchase New.
                </Text>
              )}

              {addError && (
                <View className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800">
                  <Icon
                    name="error"
                    size={14}
                    className="text-red-500 dark:text-red-400 shrink-0"
                  />
                  <Text className="text-xs text-red-600 dark:text-red-400">{addError}</Text>
                </View>
              )}

              <View className="flex gap-3 pt-2">
                <Button
                  variant="success"
                  size="auto"
                  onClick={onClickAddExisting}
                  disabled={isAdding || !addExistingFormData.phoneNumber || !canAddExisting}
                  className="flex-1 justify-center"
                >
                  {isAdding ? (
                    <Icon
                      name="loader"
                      size={14}
                      className="animate-spin"
                    />
                  ) : (
                    <Icon
                      name="add"
                      size={14}
                    />
                  )}
                  Add
                </Button>
                <Button
                  variant="neutral"
                  size="auto"
                  onClick={closeAddDialog}
                  className="flex-1 justify-center"
                >
                  Cancel
                </Button>
              </View>
            </Form>
          )}
        </View>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        isOpen={!!editingPhoneNumber}
        onClose={() => {
          setEditingPhoneNumber(null);
          resetEditForm();
        }}
        title="Edit Phone Number"
      >
        <Form
          data={editFormData}
          onChange={onChangeEdit}
          className="p-4"
        >
          <InputField
            name="friendlyName"
            label="Friendly Name"
            placeholder="Enter a friendly name"
          />

          <View className="flex gap-3 pt-2">
            <Button
              variant="success"
              size="auto"
              onClick={onClickUpdate}
              className="flex-1 justify-center"
            >
              <Icon
                name="check"
                size={14}
              />
              Update
            </Button>
            <Button
              variant="neutral"
              size="auto"
              onClick={() => {
                setEditingPhoneNumber(null);
                resetEditForm();
              }}
              className="flex-1 justify-center"
            >
              Cancel
            </Button>
          </View>
        </Form>
      </Dialog>

      {/* Remove Phone Number Dialog */}
      <Dialog
        isOpen={showRemoveDialog}
        onClose={closeRemoveDialog}
        title="Remove Phone Number"
        size="md"
      >
        <View className="flex flex-col p-6">
          <Text className="text-gray-900 dark:text-white mb-4">
            How would you like to remove &quot;{removingPhoneNumber?.friendlyName || removingPhoneNumber?.phoneNumber}&quot;?
          </Text>
          <View className="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-400">
            <Text>
              <strong>Delete</strong> removes the number from your organization but keeps it with the telephony provider.
            </Text>
            <Text>
              <strong>Delete &amp; Release</strong> also releases the number back to the provider. This cannot be undone.
            </Text>
          </View>
        </View>
        <View className="flex items-center justify-end p-6 gap-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="neutral"
            size="auto"
            onClick={closeRemoveDialog}
            disabled={isDeleting || isReleasing}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="auto"
            onClick={confirmDelete}
            disabled={isDeleting || isReleasing}
          >
            {isDeleting ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="trash"
                size={14}
              />
            )}
            Delete
          </Button>
          <button
            type="button"
            onClick={confirmRelease}
            disabled={isDeleting || isReleasing}
            className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/70 disabled:opacity-50"
          >
            {isReleasing ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="trash"
                size={14}
              />
            )}
            Delete &amp; Release
          </button>
        </View>
      </Dialog>
    </OrganizationSettingsTab>
  );
};
